import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import { Card, Empty, ErrorState, Input, Loading, Stat } from '../components/ui.jsx';
import { DocumentActions } from '../components/DocumentActions.jsx';
import { PaymentForm } from './Parties.jsx';
import { IconWallet } from '../components/Icons.jsx';
import { useApi } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { daysAgo, fileSlug, formatDate, money, num, today } from '../lib/format.js';
import { statementText } from '../lib/whatsapp.js';

const COPY = {
  customer: {
    ...t.party.customers,
    endpoint: '/api/customers',
    back: '/customers',
    closingLabel: t.party.customers.statementOwed,
  },
  supplier: {
    ...t.party.suppliers,
    endpoint: '/api/suppliers',
    back: '/suppliers',
    closingLabel: t.party.suppliers.statementOwed,
  },
  exchange: {
    endpoint: '/api/exchange/companies',
    back: '/exchange',
    closingLabel: t.exchange.balanceLabel,
    note: t.exchange.accountNote,
  },
  shareholder: {
    endpoint: '/api/shareholders',
    back: '/shareholders',
    closingLabel: t.shareholder.balanceLabel,
    note: t.shareholder.accountNote,
  },
};

/**
 * The account statement shared by every party type: customers, suppliers,
 * and exchange companies. Balances settle differently for each - customers
 * and suppliers pay down through the generic `/api/payments` endpoint, but
 * an exchange company's balance only moves
 * through its own deposit/withdrawal/transfer/buy/sell transactions, which
 * need their own kind-dependent form. `headerAction` lets a caller swap the
 * default "Payment" button for something else entirely; when it is not
 * given, the page behaves exactly as it always has.
 */
export default function Statement({ type, headerAction }) {
  const copy = COPY[type];
  const { id } = useParams();
  const { settings } = useAuth();
  const toast = useToast();

  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [paying, setPaying] = useState(false);

  const party = useApi(`${copy.endpoint}/${id}`);
  const { data, error, loading, reload } = useApi(`${copy.endpoint}/${id}/statement`, { from, to });
  const docRef = useRef(null);

  const name = party.data?.name || data?.party?.name || '';

  return (
    <>
      <PageHeader
        title={name || t.statement.title}
        subtitle={party.data?.phone || t.statement.title}
        back={copy.back}
        actions={
          headerAction
            ? headerAction({ party: party.data, reload: () => { party.reload(); reload(); } })
            : (
              <button type="button" className="btn btn-sm btn-primary no-print" onClick={() => setPaying(true)}>
                <IconWallet /> {t.party.payment}
              </button>
            )
        }
      />

      <div className="page">
        {party.data && (
          <div className="stat-grid">
            <Stat
              label={`${copy.closingLabel} (${t.report.iqd})`}
              value={money(party.data.balance_iqd, 'IQD')}
              tone={Number(party.data.balance_iqd) > 0 ? 'danger' : 'success'}
            />
            <Stat
              label={`${copy.closingLabel} (${t.report.usd})`}
              value={money(party.data.balance_usd, 'USD')}
              tone={Number(party.data.balance_usd) > 0 ? 'danger' : 'success'}
            />
          </div>
        )}

        <Card>
          <div className="filters">
            <Input label={t.common.from} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Input label={t.common.to} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <div className="field">
              <label>&nbsp;</label>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setFrom('');
                  setTo('');
                }}
              >
                {t.common.allTime}
              </button>
            </div>
          </div>
          <p className="small muted mt-0">{copy.note || t.statement.note}</p>
        </Card>

        <ErrorState error={error} onRetry={reload} />
        {loading && !data && <Loading />}

        {data && (
          <>
            <DocumentActions
              targetRef={docRef}
              paged
              filename={`statement-${fileSlug(name, id)}.pdf`}
              whatsappText={statementText(data, settings)}
              phone={party.data?.phone}
              countryCode={settings.whatsapp_country_code || '964'}
            />

            <div className="doc" ref={docRef}>
              <div className="doc-head">
                <img src="/logo.png" alt="" className="doc-logo" />
                <div className="brand">
                  <div className="name">{settings.facility_name || t.app.name}</div>
                  <div className="contact">
                    {settings.facility_address}
                    {settings.facility_address && settings.facility_phone ? ' · ' : ''}
                    {settings.facility_phone}
                  </div>
                </div>
                <div className="ref">
                  <div className="kind">{t.doc.accountStatement}</div>
                  <div className="no">{name}</div>
                  <div className="contact">
                    {data.from ? formatDate(data.from) : t.statement.start} — {data.to ? formatDate(data.to) : t.statement.today}
                  </div>
                </div>
              </div>

              {data.entries.length === 0 ? (
                <Empty title={t.statement.noTransactions} />
              ) : (
                <div className="table-wrap" style={{ marginTop: 12 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>{t.common.date}</th>
                        <th>{t.statement.detail}</th>
                        <th className="num">{t.statement.chargeIqd}</th>
                        <th className="num">{t.statement.paidIqd}</th>
                        <th className="num">{t.statement.balanceIqd}</th>
                        <th className="num">{t.statement.chargeUsd}</th>
                        <th className="num">{t.statement.paidUsd}</th>
                        <th className="num">{t.statement.balanceUsd}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={2}>
                          <em>{t.statement.opening}</em>
                        </td>
                        <td className="num">-</td>
                        <td className="num">-</td>
                        <td className="num strong">{num(data.opening.iqd)}</td>
                        <td className="num">-</td>
                        <td className="num">-</td>
                        <td className="num strong">{num(data.opening.usd)}</td>
                      </tr>
                      {data.entries.map((entry, index) => (
                        <tr key={`${entry.kind}-${entry.ref_id}-${index}`}>
                          <td>{formatDate(entry.entry_date)}</td>
                          <td className="wrap">{entry.description}</td>
                          <td className="num">{dash(entry.charge_iqd)}</td>
                          <td className="num">{dash(entry.paid_iqd)}</td>
                          <td className="num strong">{num(entry.balance_iqd)}</td>
                          <td className="num">{dash(entry.charge_usd)}</td>
                          <td className="num">{dash(entry.paid_usd)}</td>
                          <td className="num strong">{num(entry.balance_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="doc-total">
                <span className="k">{copy.closingLabel}</span>
                <span className="v">
                  {money(data.closing.iqd, 'IQD')}
                  {Number(data.closing.usd) !== 0 && (
                    <>
                      {' · '}
                      {money(data.closing.usd, 'USD')}
                    </>
                  )}
                </span>
              </div>

              <div className="doc-foot">
                <span>{settings.receipt_footer || ''}</span>
                <span className="issued">{t.statement.entryCount(data.entries.length)}</span>
              </div>
            </div>
          </>
        )}
      </div>

      {!headerAction && paying && party.data && (
        <PaymentForm
          type={type}
          party={party.data}
          onClose={() => setPaying(false)}
          onSaved={() => {
            setPaying(false);
            toast.success(t.party.paymentSaved);
            party.reload();
            reload();
          }}
        />
      )}
    </>
  );
}

const dash = (value) => (Number(value) === 0 ? '-' : num(value));
